import React, { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { AssetsAudio } from '../../assets';
import { AssetSvg } from '../../assets';
import { logger } from '../../utils';
import {
  AppConstants,
  AppRouteUi,
  CallType,
  DefaultImageType,
  FileBucketURL,
  CallStatusType,
} from '../../configs';
import { AudioDeviceContext } from '../../core';
import {
  ActionVideoCall,
  CallContext,
  SocketContext,
  StorageAuth,
  useAppSelector,
  VRIAppStateType
} from '../../core';
import { CallDurationCounter, ModalIvrCustomField } from '../../features';
import { ActionAudioCall } from '../../core/Redux/Slices/sliceCall';
import { useFunctionalityCalls } from '../hooks/functionalityCalls';
import {
  useFunctionalityGetCustomInput,
  useFunctionalityImageView,
} from '../hooks';
import { ModalHangupCall } from '../../features/modals/common/hangupCallModal';
import useFunctionalityMediaPermissions from '../hooks/mediaPermission/functionalityMediaPermission';
import { ITypeCustomInput, OperatorConferenceCalls } from '../../models';
import { ModalOutboundCallDial } from '../../features/modals/common/outboundCallDialModal';
import { NetworkConnection } from '../../components';
import { MediaPermission } from '../../utils';
import { Accordion, Card, Button, Badge, ListGroup, useAccordionToggle } from 'react-bootstrap';
import { OpiConferenceSidebar } from '../../features/Common/Components/OpiConferenceSidebar';
import { useLiveTranscription } from '../hooks/useLiveTranscription';
import { TranscriptionBox } from '../../components/common/TranscriptionBox';
import { TranscriptionSidebar } from '../../components/common/TranscriptionSidebar';
import { TranscriptionToggleButton } from '../../components/common/TranscriptionToggleButton';

export function PageIncomingCall(): JSX.Element {
  const history = useHistory();
  const [isAudioEnabled, setAudioEnabled] = useState(true);
  const { callinfo, isAccepted, setIsAccepted } = useContext(CallContext);
  const { socketConnection } = useContext(SocketContext);
  const { device, activeCall } = useContext(AudioDeviceContext);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const dispatch = useDispatch();
  const data = useAppSelector((state: VRIAppStateType) => ({
    audioCallStatus: state.audioCall.audioCallStatus,
    audioOperatorCalls: state.audioCall.audioOperatorCalls,
    audioConferenceCalls: state.audioCall.audioConferenceCalls,
    userId: state.auth.profile?.id,
    userRole: state.auth.profile?.userRole,
    sessionId: state.auth.sessionId,
    isBackdatedVersion: state.common.isBackdatedVersion,
  }));
  const isIVRCall = (() => {
    const callType = callinfo?.customParameters?.get('Type');
    return callType === 'IVR' || callType === 'ThreeWayIVR';
  })();

  const [show, setShow] = useState(false);
  const [showOutboundModal, setShowOutboundModal] = useState(false);
  const [showIvrCustomFieldModal, setShowIvrCustomFieldModal] = useState(false);
  const [hasIvrCustomFieldModalBeenOpened, setHasIvrCustomFieldModalBeenOpened] = useState(false);
  const [lastSubmittedIvrCustomFieldValues, setLastSubmittedIvrCustomFieldValues] = useState<Record<string, any>>({});
  const [activeKey, setActiveKey] = useState<string | undefined>(undefined);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [operatorsCalledList, setOperatorsCalledList] = useState<OperatorConferenceCalls>([]);
  const [showAddedOperatorStatus, setShowAddedOperatorStatus] = useState(true);
  const [isTranscriptSidebarOpen, setIsTranscriptSidebarOpen] = useState(false);
  const [isTranscriptBoxVisible, setIsTranscriptBoxVisible] = useState(false);
  const [hasTranscriptAutoShown, setHasTranscriptAutoShown] = useState(false);
  const [isCompactTranscriptLayout, setIsCompactTranscriptLayout] = useState(
    typeof window !== 'undefined' ? window.innerHeight < 720 : false
  );
  const [shouldShowTranscription, setShouldShowTranscription] = useState<boolean | null>(null);
  const transcriptionVisibilityTimeoutRef = useRef<number | null>(null);
  const transcriptionVisibilityStartedAtRef = useRef<number | null>(null);
  const transcriptionVisibilityAttemptRef = useRef(0);

  const { onUpdate, onComplete, onStartInitiate, onCallConferenceParticipant, onSendOutboundDtmf, onOutboundHangupLep, onGetTranscriptionVisibility } =
    useFunctionalityCalls();
  const audioCallStatusRef = React.useRef(data.audioCallStatus);
  audioCallStatusRef.current = data.audioCallStatus;
  const interval = useRef<any>(null);


  const homePage = (() => {
    if (StorageAuth.ProfileData?.userRole === AppConstants.UserRoles.Operator) {
      return AppRouteUi.CallLog.Root;
    } else {
      return AppRouteUi.Home.Root;
    }
  })();
  !callinfo?.customParameters && history.push(homePage);

  useEffect(() => {
    dispatch(ActionAudioCall.ClearAudioConferenceCalls());
    dispatch(ActionAudioCall.ClearOperatorConferenceCalls());
  }, []);

  const callSid = callinfo?.customParameters?.get('CallSid') ?? null;
  const callId = callinfo?.customParameters?.get('CallId') ?? null;
  const parentCallSid =
    callinfo?.customParameters?.get('ParentCallSid') ??
    callinfo?.customParameters?.get('ParentCallSID') ??
    callinfo?.customParameters?.get('CallSid') ??
    null;

  // Reset when a new call arrives
  React.useLayoutEffect(() => {
    if (callSid) {
      setIsAccepted(false);
      setLastSubmittedIvrCustomFieldValues({});
      setHasIvrCustomFieldModalBeenOpened(false);
      setIsTranscriptBoxVisible(false);
      setHasTranscriptAutoShown(false);
      setShouldShowTranscription(null);
      transcriptionVisibilityAttemptRef.current = 0;
      transcriptionVisibilityStartedAtRef.current = null;
      if (transcriptionVisibilityTimeoutRef.current) {
        clearTimeout(transcriptionVisibilityTimeoutRef.current);
        transcriptionVisibilityTimeoutRef.current = null;
      }
    }
  }, [callSid]);

  // Reset on unmount
  useEffect(() => {
    return () => setIsAccepted(false);
  }, []);


  useEffect(() => {
    if (data.audioOperatorCalls?.length) {
      const latestStatus = data.audioOperatorCalls[data.audioOperatorCalls.length - 1]?.operatorStatus.current;

      const callStatuses: CallStatusType[] = [
        CallStatusType.Missed,
        CallStatusType.Rejected,
        CallStatusType.Failed,
        CallStatusType.Canceled,
        CallStatusType.Abort,
        CallStatusType.Completed,
      ];

      if (latestStatus && callStatuses.includes(latestStatus)
      ) {
        setShowAddedOperatorStatus(true);
        const timer = setTimeout(() => setShowAddedOperatorStatus(false), 7000);
        return () => clearTimeout(timer);
      } else {
        setShowAddedOperatorStatus(true);
      }
    }
  }, [data.audioOperatorCalls]);


  useEffect(() => {
    const anyRinging = data.audioConferenceCalls?.some(
      (call) => call.status?.current === CallStatusType.OutboundCallRinging
    );

    if (anyRinging) {
      if (!audioRef.current) {
        audioRef.current = new Audio("/assets/audio/ringing_conference_participant.wav");
        audioRef.current.volume = 0.1;
        audioRef.current.loop = true;
      }

      audioRef.current.play().catch((err) => {
        console.warn("Could not play audio:", err);
      });
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, [data.audioConferenceCalls]);

  useEffect(() => {
    if (isAccepted) {
      dispatch(ActionAudioCall.SetAudioCallStartTime(new Date().toString()));
    }
  }, [isAccepted]);

  // useEffect(() => {
  //   console.log("audioOperatorCalls has been updated to: ", data.audioOperatorCalls);

  // }, [data.audioOperatorCalls])


  useEffect(() => {
    !isIVRCall && onUpdate(callinfo.customParameters?.get('CallId'), {
      callStatus: CallStatusType.Ringing,
      callType: CallType.Audio,
      eventTime: new Date().toISOString(),
      isCallStarted: false,
      callerUserId: callinfo.customParameters?.get('CallerUid'),
    });
  }, []);

  const getOperatorStatusLabel = (status: CallStatusType, operatorName?: string): string | null => {

    if (status === CallStatusType.Initiated) {
      return "Initiated call to interpreter " + operatorName;
    }

    else if (status === CallStatusType.Ringing) {
      return "Ringing to interpreter " + operatorName;
    }

    else if (status === CallStatusType.Missed) {
      return "Interpreter " + operatorName + " has missed the call";
    }

    else if (status === CallStatusType.Rejected) {
      return "Interpreter " + operatorName + " has rejected the call";
    }

    else if (status === CallStatusType.Received || status === CallStatusType.Established) {
      return "Interpreter " + operatorName + " is incall";
    }

    else if (status === CallStatusType.Failed || status === CallStatusType.Canceled || status === CallStatusType.Abort) {
      return "Call to Interpreter " + operatorName + " has failed";
    }

    else if (status === CallStatusType.Completed) {
      return "Call with Interpreter " + operatorName + " is completed";
    }

    else {
      console.log("unhandled operator status: ", status);
      return null
    };
  };


  const { stopBothVideoAndAudio } = useFunctionalityMediaPermissions();
  const outBoundMessageMapping = (callStatusType?: CallStatusType) => {
    let message = '';
    switch (callStatusType) {
      case CallStatusType.OutboundCallCompleted:
        message = 'Outbound call completed';
        break;
      case CallStatusType.OutboundCallInitiated:
        message = 'Making outbound call';
        break;
      case CallStatusType.OutboundCallNotAnswered:
        message = 'No answer';
        break;
      case CallStatusType.OutboundCallReceived:
        message = 'Connected with outbound caller';
        break;
      case CallStatusType.OutboundCallRinging:
        message = 'Ringing for outbound caller';
        break;
      case CallStatusType.OutboundCallNotReceived:
        message = 'Outbound call is not received';
        break;
    }
    return message;
  };
  useEffect(() => {
    interval.current = setTimeout(
      (function (x) {
        return function () {
          if (
            audioCallStatusRef?.current?.current === CallStatusType.Ringing &&
            audioCallStatusRef?.current?.prev === CallStatusType.Initiated
          ) {
            callinfo.reject();

            onUpdate(callinfo.customParameters?.get('CallId'), {
              callType: CallType.Audio,
              callStatus: CallStatusType.Missed,
              eventTime: new Date().toISOString(),
              isCallStarted: false,
            });
            history.push({
              pathname:
                data.userRole === AppConstants.UserRoles.Operator
                  ? AppRouteUi.CallLog.Root
                  : AppRouteUi.Home.Root,
            });
          }
        };
      })(data),
      40000
    );

    return () => {
      clearTimeout(interval.current);
    };
  }, []);

  const audioCallCompleted = () => {
    dispatch(
      ActionAudioCall.SetAudioCallStatus({
        prev: CallStatusType.Established,
        current: CallStatusType.Completed,
      })
    );
    dispatch(ActionAudioCall.ClearAudioConferenceCalls());
    setIsAccepted(false);
    device?.disconnectAll();
  };
  const audioCallRejected = (socketConnection: any) => {
    clearTimeout(interval.current);
    const callerId = callinfo.customParameters?.get('CallerUid');
    const callid = callinfo.customParameters?.get('CallId');
    dispatch(
      ActionVideoCall.SetCallStatus({
        prev: CallStatusType.Ringing,
        current: CallStatusType.Rejected,
      })
    );
    !isIVRCall && onUpdate(callid, {
      callStatus: CallStatusType.Rejected,
      eventTime: new Date().toISOString(),
      isCallStarted: false,
    });
    stopBothVideoAndAudio();
    dispatch(ActionAudioCall.ClearAudioConferenceCalls());

    history.push({
      pathname:
        data.userRole === AppConstants.UserRoles.Operator
          ? AppRouteUi.CallLog.Root
          : AppRouteUi.Home.Root,
    });
  };
  const [participantState, setParticipantState] = React.useState({
    prev: 0,
    current: 1,
  });
  const { defaultImageLoad, imageView } = useFunctionalityImageView();

  const terminalStatuses: CallStatusType[] = [
    CallStatusType.Completed,
    CallStatusType.Canceled,
    CallStatusType.Failed,
    CallStatusType.Abort,
    CallStatusType.Rejected,
    CallStatusType.Missed,
  ];
  const isCallActive =
    !!isAccepted && !terminalStatuses.includes(data.audioCallStatus?.current ?? CallStatusType.Zero);

  const {
    history: transcriptHistory,
    partial: transcriptPartial,
    error: transcriptError,
    reset: resetTranscript,
  } = useLiveTranscription({ callSid: parentCallSid, enabled: isCallActive });

  const toTimestampMs = useCallback((atTicks?: number) => {
    if (!atTicks) return undefined;
    if (atTicks > 1e14) return Math.floor(atTicks / 10000);
    if (atTicks > 1e11) return Math.floor(atTicks);
    return undefined;
  }, []);

  const [getCustomInputData, setGetCustomInputData] = useState<
    ITypeCustomInput[]
  >([]);
  const { onCustomInputData } = useFunctionalityGetCustomInput(
    callinfo.customParameters?.get('CallerUid')
  );


  const OnReceivedPermissionHandler = async () => {
    clearTimeout(interval.current);
    if (await MediaPermission.getStatus({ audio: true })) {
      dispatch(
        ActionVideoCall.SetCallStatus({
          prev: CallStatusType.Ringing,
          current: CallStatusType.Received,
        })
      );
      const callToAccept = activeCall ?? callinfo;
      logger({
        message: "[IncomingCall] Accept pressed",
        data: {
          callinfoSid: callinfo?.customParameters?.get('CallSid'),
          activeCallSid: activeCall?.customParameters?.get('CallSid'),
          acceptingSid: callToAccept?.customParameters?.get('CallSid'),
        },
      });
      if (callToAccept?.accept) {
        callToAccept.accept();
      } else {
        logger({ message: "[IncomingCall] No call instance available to accept" });
      }
      if (
        callinfo?.customParameters?.get('Type') === 'IVR' ||
        callinfo?.customParameters?.get('Type') === 'ThreeWayIVR'
      ) {
        // show modal
        onCustomInputData()?.then((res) => {
          if (res?.data?.length) {
            setShowIvrCustomFieldModal(true);
            setGetCustomInputData(res.data);
          } else {
            setShowIvrCustomFieldModal(false);
          }
        });
        if (callinfo?.customParameters?.get('Type') === 'ThreeWayIVR') {
          onStartInitiate(
            callinfo?.customParameters?.get('CallSid'),
            data.sessionId ?? '00-00-00'
          );
        }
      }
    } else {
      callRejectHandler();
      //console.trace("Redirecting:");
      history.push({
        pathname: AppRouteUi.OutgoingCallFailedForMic.Root,
      });
    }
  };

  const callRejectHandler = () => {
    dispatch(
      ActionAudioCall.SetAudioCallStatus({
        prev: CallStatusType.Ringing,
        current: CallStatusType.Rejected,
      })
    );

    callinfo.reject();
    dispatch(ActionAudioCall.ClearAudioConferenceCalls());
    audioCallRejected(socketConnection); //for rejecting incoming call
  };


  const handleDisconnect = useCallback(() => {
    logger({ message: 'Disconnecting Call' });
    device?.disconnectAll();
    dispatch(ActionAudioCall.ClearAudioConferenceCalls());

    history.push({
      pathname: AppRouteUi.Home.Root,
    });
  }, [history]);

  useEffect(() => {
    if (
      data.audioCallStatus?.current == CallStatusType.Received ||
      data.audioCallStatus?.current == CallStatusType.Rejected
    ) {
      clearTimeout(interval.current);
    }

  }, [data?.audioCallStatus?.current]);

  const onEndedPlay = (ev: any) => {
    ev.target.currentTime = 0;
    ev.target.play();
  };

  useEffect(() => {
    if (data.audioCallStatus?.current === CallStatusType.Ringing) {
      try {
        window.history.pushState(null, document.title, window.location.href);
        window.addEventListener('popstate', function (event) {
          window.history.pushState(null, document.title, window.location.href);
        });
      } catch (err) {
        console.error(err);
      }
    }
  }, [data.audioCallStatus?.current]);
  useEffect(() => {
    if (data.audioCallStatus?.current === CallStatusType.Ringing) {
      window.onbeforeunload = function () {
        return true;
      };
    }
    return () => {
      window.onbeforeunload = null;
    };
  }, [data.audioCallStatus?.current]);

  useEffect(() => {
    if (!isIVRCall) {
      return;
    }
    const handleCallCancel = () => {
      callinfo?.reject();
      history.push({
        pathname: homePage,
      });
    };

    callinfo?.on('cancel', handleCallCancel);

    return () => {
      callinfo?.removeListener('cancel', handleCallCancel);
    };
  }, [callinfo, isIVRCall, history, homePage]);

  useEffect(() => {
    return () => {
      callinfo?.reject?.();
    };
  }, [callinfo]);

  useEffect(() => {
    if (showIvrCustomFieldModal && !hasIvrCustomFieldModalBeenOpened) {
      setHasIvrCustomFieldModalBeenOpened(true);
    }
  }, [showIvrCustomFieldModal, hasIvrCustomFieldModalBeenOpened]);

  useEffect(() => {
    if (!isCallActive) {
      resetTranscript();
    }
  }, [isCallActive, resetTranscript]);

  useEffect(() => {
    if (!isAccepted) {
      setIsTranscriptBoxVisible(false);
      setHasTranscriptAutoShown(false);
      setShouldShowTranscription(null);
      transcriptionVisibilityAttemptRef.current = 0;
      transcriptionVisibilityStartedAtRef.current = null;
      if (transcriptionVisibilityTimeoutRef.current) {
        clearTimeout(transcriptionVisibilityTimeoutRef.current);
        transcriptionVisibilityTimeoutRef.current = null;
      }
      return;
    }
    if (shouldShowTranscription !== true) return;
    if (hasTranscriptAutoShown) return;
    if (transcriptHistory.length || transcriptPartial) {
      if (isCompactTranscriptLayout) {
        setIsTranscriptSidebarOpen(true);
      } else {
        setIsTranscriptBoxVisible(true);
      }
      setHasTranscriptAutoShown(true);
    }
  }, [isAccepted, shouldShowTranscription, hasTranscriptAutoShown, transcriptHistory.length, transcriptPartial, isCompactTranscriptLayout]);

  useEffect(() => {
    if (!isAccepted) {
      setIsTranscriptSidebarOpen(false);
    }
  }, [isAccepted]);

  useEffect(() => {
    const handleResize = () => {
      setIsCompactTranscriptLayout(window.innerHeight < 720);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (isCompactTranscriptLayout) {
      setIsTranscriptBoxVisible(false);
    } else {
      setIsTranscriptSidebarOpen(false);
    }
  }, [isCompactTranscriptLayout]);

  useEffect(() => {
    if (!isAccepted || !parentCallSid || !data.userId) {
      return;
    }

    const resolvedParentCallSid = parentCallSid;
    const resolvedUserId = data.userId;

    let isCancelled = false;
    const hardStopMs = 150000;

    const getRetryDelayMs = (attempt: number) => {
      if (attempt < 5) return 1000;
      if (attempt < 10) return 2000;
      return 3000;
    };

    const clearPendingRetry = () => {
      if (transcriptionVisibilityTimeoutRef.current) {
        clearTimeout(transcriptionVisibilityTimeoutRef.current);
        transcriptionVisibilityTimeoutRef.current = null;
      }
    };

    const scheduleRetry = () => {
      const startedAt = transcriptionVisibilityStartedAtRef.current;
      if (!startedAt) return;
      const elapsedMs = Date.now() - startedAt;
      if (elapsedMs >= hardStopMs) {
        setShouldShowTranscription(false);
        setIsTranscriptBoxVisible(false);
        setIsTranscriptSidebarOpen(false);
        return;
      }
      const attempt = transcriptionVisibilityAttemptRef.current;
      const delayMs = getRetryDelayMs(attempt);
      transcriptionVisibilityTimeoutRef.current = window.setTimeout(() => {
        void pollVisibility();
      }, delayMs);
    };

    const pollVisibility = async () => {
      if (isCancelled) return;
      if (!transcriptionVisibilityStartedAtRef.current) {
        transcriptionVisibilityStartedAtRef.current = Date.now();
      }

      const response = await onGetTranscriptionVisibility(resolvedParentCallSid, resolvedUserId);
      if (isCancelled || !response) {
        transcriptionVisibilityAttemptRef.current += 1;
        scheduleRetry();
        return;
      }

      if (response.isResolved) {
        setShouldShowTranscription(!!response.showTranscription);
        if (!response.showTranscription) {
          setIsTranscriptBoxVisible(false);
          setIsTranscriptSidebarOpen(false);
        }
        clearPendingRetry();
        return;
      }

      transcriptionVisibilityAttemptRef.current += 1;
      scheduleRetry();
    };

    setShouldShowTranscription(null);
    clearPendingRetry();
    transcriptionVisibilityAttemptRef.current = 0;
    transcriptionVisibilityStartedAtRef.current = Date.now();
    void pollVisibility();

    return () => {
      isCancelled = true;
      clearPendingRetry();
    };
  }, [isAccepted, parentCallSid, data.userId, onGetTranscriptionVisibility]);

  return (
    <>
      <div className="site-call-page ">
        {isAccepted && shouldShowTranscription === true && isCompactTranscriptLayout && (
          <TranscriptionSidebar
            isOpen={isTranscriptSidebarOpen}
            setIsOpen={setIsTranscriptSidebarOpen}
            lines={transcriptHistory.map((line) => ({ text: line.text, role: line.role ?? null }))}
            partialText={transcriptPartial?.text ?? null}
            partialRole={transcriptPartial?.role ?? null}
          />
        )}
        <div className="site-call-page-wrapper">
          <div className="container-fluid h-100">
            <div className="row d-flex p-2" style={{ height: "60px" }}>
              <div className="d-flex align-items-center">
                {(isAccepted && hasIvrCustomFieldModalBeenOpened) ? (
                  <button
                    className="btn text-site-dark text-uppercase svg-color d-flex align-items-center"
                    onClick={() => setShowIvrCustomFieldModal(true)}
                    style={{
                      height: '30px',
                      padding: '0 12px',
                      borderRadius: '9px',
                      border: '1px solid rgba(255,255,255,0.14)',
                      backgroundColor: 'rgba(255,255,255,0.08)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
                      color: '#d5deef',
                      textTransform: 'none',
                      transition: 'background-color 160ms ease, border-color 160ms ease, color 160ms ease',
                    }}
                  >
                    <span className="mr-2 d-flex align-items-center" style={{ color: 'inherit' }}>
                      <AssetSvg.Edit />
                    </span>
                    <span className="font-sz-10" style={{ fontWeight: 600, letterSpacing: '0.04em' }}>Edit Fields</span>
                  </button>
                ) : null}
                {isAccepted && shouldShowTranscription === true && (
                  <TranscriptionToggleButton
                    isOpen={isCompactTranscriptLayout ? isTranscriptSidebarOpen : isTranscriptBoxVisible}
                    onClick={() => {
                      if (isCompactTranscriptLayout) {
                        setIsTranscriptSidebarOpen((prev) => !prev);
                      } else {
                        setIsTranscriptBoxVisible((prev) => !prev);
                      }
                      setHasTranscriptAutoShown(true);
                    }}
                    className="btn text-site-dark text-uppercase svg-color d-flex align-items-center ml-2"
                  />
                )}
              </div>
            </div>
            <div className="row justify-content-center align-items-center Page-call">

              <div className="col-6 text-center d-flex align-items-center justify-content-center pt-4 pb-4">
                <ul className=" babelCallAnimation bg-surface caller-image">
                  <img
                    src={`${FileBucketURL.Root}${callinfo.customParameters?.get(
                      'CallerUid'
                    )}?${imageView.fileTimeStamp}`}
                    alt=""
                    onError={(ev) => {
                      defaultImageLoad(
                        ev,
                        DefaultImageType.UserProfile,
                        '?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260'
                      );
                    }}
                    className="img-fluid"
                  />
                  <li></li>
                  <li></li>
                  <li></li>
                  <li></li>
                  <li></li>
                </ul>
              </div>
              {data.audioCallStatus?.current == CallStatusType.Completed && (
                <div className="col-12 text-center  text-white p-3">
                  <h5 className="m-0">
                    Preparing Call Summary. Please wait...
                  </h5>
                </div>
              )}
              {data.audioCallStatus?.current !== CallStatusType.Completed &&
                data.audioCallStatus?.prev !== CallStatusType.Established && (
                  <>
                    <div className="col-12 text-center text-white p-3">
                      {isAccepted ? (
                        <p className="mb-3 pb-3 h5">
                          <CallDurationCounter
                            participantState={participantState}
                          />
                        </p>
                      ) : (
                        <p className="mb-3 pb-3 h5">Incoming OPI call</p>
                      )}
                      {data.audioCallStatus?.current ==
                        CallStatusType.OutboundCallInitiated ||
                        data.audioCallStatus?.current ==
                        CallStatusType.OutboundCallNotAnswered ||
                        data.audioCallStatus?.current ==
                        CallStatusType.OutboundCallRinging ||
                        data.audioCallStatus?.current ==
                        CallStatusType.OutboundCallCompleted ||
                        data.audioCallStatus?.current ==
                        CallStatusType.OutboundCallReceived}
                      <h4 className="m-0">
                        {outBoundMessageMapping(data.audioCallStatus?.current)}
                      </h4>
                      <h2 className="pb-4">
                        {callinfo.customParameters?.get('CallerName')}
                      </h2>
                      {
                        (
                          <h5 className="m-0">
                            {callinfo.customParameters?.get('CallerCompanyName')}
                          </h5>
                        )
                      }
                      <h5 className="m-0">
                        {' '}
                        {callinfo.customParameters?.get('OperatorLanguageName')}
                      </h5>

                      {(data.audioOperatorCalls?.length) ? (
                        <div className="mt-4">
                          {data.audioOperatorCalls[data.audioOperatorCalls.length - 1].operatorName && showAddedOperatorStatus &&
                            <span style={{ backgroundColor: "rgba(175, 0, 0, 0.4)", borderRadius: "20px", padding: "5px 10px" }}>
                              {getOperatorStatusLabel(
                                data.audioOperatorCalls[data.audioOperatorCalls.length - 1].operatorStatus.current || CallStatusType.Initiated,
                                data.audioOperatorCalls[data.audioOperatorCalls.length - 1].operatorName
                              )}
                            </span>
                          }
                        </div>
                      ) : (<></>)}
                    </div>
                    {isAccepted && shouldShowTranscription === true && !isCompactTranscriptLayout && isTranscriptBoxVisible && (
                      <div className="col-12 text-center">
                        <TranscriptionBox
                          errorText={transcriptError}
                          warningText={!parentCallSid ? "Transcript unavailable: Parent CallSid not found." : null}
                          lines={transcriptHistory.map((line) => ({
                            seq: line.seq,
                            text: line.text,
                            tsMs: toTimestampMs(line.atTicks ?? undefined),
                            role: line.role ?? null,
                          }))}
                          partialText={transcriptPartial?.text ?? null}
                          partialRole={transcriptPartial?.role ?? null}
                        />
                      </div>
                    )}
                    <div className="h-15 w-100">
                      <NetworkConnection hangupHandler={audioCallCompleted} />
                    </div>
                    {isAccepted ? (
                      <div className="col-12 text-center p-3">
                        <div className="container-fluid position-relative d-flex justify-content-end align-items-end">
                          <div className="position-absolute d-flex align-items-center justify-content-center" style={{
                            paddingBottom: "1px",
                            bottom: '0%',
                            left: '50%',
                            transform: 'translateX(-50%)'
                          }}>
                            <button
                              className="btn  mx-4  callPage-controlBtn btn-red"
                              role="button"
                              onClick={() => {
                                setShow(true);
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                              >
                                <path d="M511.2 387l-23.25 100.8c-3.266 14.25-15.79 24.22-30.46 24.22C205.2 512 0 306.8 0 54.5c0-14.66 9.969-27.2 24.22-30.45l100.8-23.25C139.7-2.602 154.7 5.018 160.8 18.92l46.52 108.5c5.438 12.78 1.77 27.67-8.98 36.45L144.5 207.1c33.98 69.22 90.26 125.5 159.5 159.5l44.08-53.8c8.688-10.78 23.69-14.51 36.47-8.975l108.5 46.51C506.1 357.2 514.6 372.4 511.2 387z" />
                              </svg>
                            </button>
                            <ModalHangupCall
                              handleShow={setShow}
                              onSubmit={() => audioCallCompleted()}
                              show={show}
                              header={'Hangup Call'}
                            />
                            {isAudioEnabled ? (
                              <button
                                type="button"
                                className="btn mx-4 callPage-controlBtn btn-green btn-audio-on"
                                onClick={() => {
                                  setAudioEnabled(false);
                                  if (activeCall) {
                                    try {
                                      activeCall.mute(true);
                                    } catch (err) {
                                      console.error("Error muting audio:", err);
                                    }
                                  }
                                }}
                              >
                                <AssetSvg.AudioOn />
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="btn mx-4 callPage-controlBtn btn-red btn-audio-off"
                                onClick={() => {
                                  setAudioEnabled(true);
                                  if (activeCall) {
                                    try {
                                      activeCall.mute(false);
                                    } catch (err) {
                                      console.error("Error unmuting audio:", err);
                                    }
                                  }
                                }}
                              >
                                <AssetSvg.AudioOff />
                              </button>
                            )}

                          </div>

                          <OpiConferenceSidebar
                            setShowOutboundModal={setShowOutboundModal}
                            isSidebarOpen={isSidebarOpen}
                            setIsSidebarOpen={setIsSidebarOpen}
                            handleHangup={onOutboundHangupLep}
                            callinfo={callinfo}
                            onCallConferenceParticipant={onCallConferenceParticipant}
                            onSendOutboundDtmf={onSendOutboundDtmf}
                            key={callinfo?.customParameters?.get("CallSid")}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="col-12 text-center p-3">
                        <div className="d-flex align-items-center justify-content-center container">
                          <button
                            className="btn mx-4  callPage-controlBtn btn-red"
                            role="button"
                            onClick={() => {
                              callRejectHandler();
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 512 512"
                            >
                              <path d="M511.2 387l-23.25 100.8c-3.266 14.25-15.79 24.22-30.46 24.22C205.2 512 0 306.8 0 54.5c0-14.66 9.969-27.2 24.22-30.45l100.8-23.25C139.7-2.602 154.7 5.018 160.8 18.92l46.52 108.5c5.438 12.78 1.77 27.67-8.98 36.45L144.5 207.1c33.98 69.22 90.26 125.5 159.5 159.5l44.08-53.8c8.688-10.78 23.69-14.51 36.47-8.975l108.5 46.51C506.1 357.2 514.6 372.4 511.2 387z" />
                            </svg>
                          </button>

                          <button
                            className="btn mx-4  callPage-controlBtn btn-green"
                            onClick={() => {
                              OnReceivedPermissionHandler();
                            }}
                            disabled={data.isBackdatedVersion}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 512 512"
                            >
                              <path d="M511.2 387l-23.25 100.8c-3.266 14.25-15.79 24.22-30.46 24.22C205.2 512 0 306.8 0 54.5c0-14.66 9.969-27.2 24.22-30.45l100.8-23.25C139.7-2.602 154.7 5.018 160.8 18.92l46.52 108.5c5.438 12.78 1.77 27.67-8.98 36.45L144.5 207.1c33.98 69.22 90.26 125.5 159.5 159.5l44.08-53.8c8.688-10.78 23.69-14.51 36.47-8.975l108.5 46.51C506.1 357.2 514.6 372.4 511.2 387z" />
                            </svg>
                          </button>

                        </div>
                      </div>
                    )}
                  </>
                )}
            </div>
          </div>
        </div>
      </div>
      <ModalIvrCustomField
        key={callId ?? 'no-call'}
        show={showIvrCustomFieldModal}
        handleShow={setShowIvrCustomFieldModal}
        callId={callinfo.customParameters?.get('CallId')}
        callerId={callinfo.customParameters?.get('CallerUid')}
        callerCompanyId={callinfo.customParameters?.get('CallerCompanyId')}
        getCustomInputData={getCustomInputData}
        initialValues={lastSubmittedIvrCustomFieldValues}
        onSubmitted={setLastSubmittedIvrCustomFieldValues}
      />
      <ModalOutboundCallDial
        handleShow={setShowOutboundModal}
        show={showOutboundModal}
        callinfo={callinfo}
        onCallConferenceParticipant={onCallConferenceParticipant}
        handleDisconnect={handleDisconnect}
        audioConferenceCalls={data.audioConferenceCalls ?? []}
      />
    </>
  );
}

